import React, { useState } from "react";
import api from "../../utils/axiosInstance";
import { useModal } from "../../context/ModalContext";

export default function SecurityTab() {
  const modal = useModal();
  const [formData, setFormData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      return modal.alert("New passwords do not match.", "error");
    }
    if (formData.newPassword.length < 6) {
      return modal.alert("New password must be at least 6 characters.", "error");
    }
    try {
      await api.put("/api/settings/security", {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      modal.alert("Password updated!", "success");
      setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      console.error(err);
      modal.alert(err?.response?.data?.message || "Error updating password", "error");
    }
  };

  const passwordsMatch = !formData.confirmPassword || formData.newPassword === formData.confirmPassword;

  return (
    <div>
      <h2 className="text-lg font-semibold text-ink-primary mb-5">Security</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column: change password */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface-card">
          <h3 className="text-sm font-semibold text-ink-primary mb-1">Change Password</h3>
          <p className="text-xs text-ink-muted mb-4">Use at least 6 characters. A strong, unique password keeps your account safe.</p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="input-group">
              <label className="input-label">Current Password</label>
              <input type="password" className="input" value={formData.currentPassword}
                onChange={e => setFormData({...formData, currentPassword: e.target.value})} required />
            </div>
            <div className="input-group">
              <label className="input-label">New Password</label>
              <input type="password" className="input" value={formData.newPassword}
                onChange={e => setFormData({...formData, newPassword: e.target.value})} required minLength={6} />
            </div>
            <div className="input-group">
              <label className="input-label">Confirm New Password</label>
              <input type="password" className={`input ${!passwordsMatch ? "input-error" : ""}`} value={formData.confirmPassword}
                onChange={e => setFormData({...formData, confirmPassword: e.target.value})} required />
              {!passwordsMatch && <p className="input-error-msg">Passwords do not match</p>}
            </div>
            <button className="btn-primary" type="submit" disabled={!passwordsMatch || !formData.confirmPassword}>Update Password</button>
          </form>
        </div>

        {/* Right column: security tips (informational) */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface-bg">
          <h3 className="text-sm font-semibold text-ink-primary mb-1">Security Tips</h3>
          <p className="text-xs text-ink-muted mb-4">Best practices to keep your Walytic account protected.</p>
          <ul className="space-y-3 text-sm text-ink-secondary">
            <li className="flex gap-2">
              <span className="text-brand mt-0.5">&bull;</span>
              <span><span className="font-medium text-ink-primary">Use a strong, unique password.</span> Avoid reusing passwords from other services. A password manager makes this effortless.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-brand mt-0.5">&bull;</span>
              <span><span className="font-medium text-ink-primary">Enable two-factor auth when available.</span> 2FA blocks most account-takeover attempts even if your password leaks.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-brand mt-0.5">&bull;</span>
              <span><span className="font-medium text-ink-primary">Rotate your password periodically.</span> Especially if you suspect a device or browser may have been compromised.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-brand mt-0.5">&bull;</span>
              <span><span className="font-medium text-ink-primary">Never share credentials.</span> Invite teammates via the Team tab instead of sharing your login.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-brand mt-0.5">&bull;</span>
              <span><span className="font-medium text-ink-primary">Sign out on shared devices.</span> Always log out when using a public or borrowed computer.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
